import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { validate } from '@adornis/validation/decorators.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import type { Gender, Religion } from '../../../db/enums/misc-enums.js';
import { throttleMiddleware } from '../../../request-middlewares/throttelMiddleware.js';
import { RegisterMode } from '../../registrationMode.js';
import { resolveRegisterStudentWithSUSForm } from '../server/registerSUSInternals/resolveRegisterStudentWithSUSForm.js';

@Entity()
export class RegisterStudentDataSorgeberechtigte extends AdornisEntity {
  static override _class = 'RegisterStudentDataSorgeberechtigte';

  static override resolveID(doc: PartialEntityData<RegisterStudentDataSorgeberechtigte>): string {
    return doc._id ?? A.getGloballyUniqueID();
  }

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  /**
   * The ID of the potential existing sorgeberechtigte. If there is none, this field is null and we need to create one for the data
   */
  @Field(() => String)
  potentialExistingSbID: Maybe<string>;

  @validate(sgNonOptional())
  @Field(() => String)
  email!: string;

  @Field(() => String)
  title!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  firstNameSB!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  lastNameSB!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  geschlecht!: Gender;

  @validate(sgNonOptional())
  @Field(() => String)
  telefonNr: Maybe<string>;

  @validate(sgNonOptional())
  @Field(() => String)
  streetAndNumber!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  zipSB!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  citySB!: string;

  @Field(() => String)
  districtSB: Maybe<string>;

  @validate(sgNonOptional())
  @Field(() => String)
  religion!: Religion;

  @Field(() => String)
  jobtitle: Maybe<string>;
}

@Entity()
export class RegisterStudentDataFiles extends AdornisEntity {
  static override _class = 'RegisterStudentDataFiles';

  @Field(() => [ID], { default: v => v ?? [''] })
  primarySchoolTranscripts!: string[];

  @Field(() => ID)
  learningDisabilities: Maybe<string>;

  @Field(() => ID)
  sorgerechtsurteil: Maybe<string>;
}

@Entity()
export class RegisterStudentData extends AdornisEntity {
  static override _class = 'RegisterStudentData';

  @validate(sgNonOptional())
  @Field(() => String)
  token!: string;

  @Field(() => Int)
  mode!: number;

  /**
   * Taken from the link: the school asked to correct an existing application. Only steers the form - the server
   * reads the flag from the token itself.
   */
  @Field(() => Boolean)
  update: Maybe<boolean>;

  /**
   * The ID of the school the student is enrolling in
   */
  @validate(sgNonOptional())
  @Field(() => String)
  schoolID!: string;

  /**
   * The grade the student is enrolling in. Not saved on the user directly but is important for the one to one conversation with the school later -> should be reflected in the event which the user picks
   */
  @validate(sgNonOptional())
  @Field(() => String)
  klassenstufe!: string;

  // #region Personal Data
  /**
   * The nesseary data for the student
   */
  @validate(sgNonOptional())
  @Field(() => String)
  firstName!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  lastName!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  geschlecht!: Gender;

  @validate(sgNonOptional())
  @Field(() => Date)
  birthday!: Date;

  @validate(sgNonOptional())
  @Field(() => String)
  placeofBirth!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  countryOfBirth!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  staatsangehoerigkeit!: string;

  @validate(sgNonOptional({ allowFalsy: true }))
  @Field(() => Boolean)
  hasSiblingsAtMartinusSchools!: boolean;

  /**
   * The optional second nationality of the student. Many persons do not have this
   */
  @Field(() => String)
  staatsangehoerigkeit2: Maybe<string>;

  @validate(sgNonOptional())
  @Field(() => String)
  streetAndNumber!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  zip!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  city!: string;

  @Field(() => String)
  district: Maybe<string>;

  @validate(sgNonOptional())
  @Field(() => String)
  religion!: Religion;

  @validate(sgNonOptional({ allowFalsy: true }))
  @Field(() => Boolean)
  familyLanguageIsGerman!: boolean;

  @Field(() => String)
  potentialExistingChildID: Maybe<string>;

  // an update keeps the interview the school already arranged
  @validate(options => {
    if (options.target.mode === RegisterMode.REGISTRATION_ELEMENTARY_HIGH && !options.target.update) {
      return sgNonOptional()(options);
    }
  })
  @Field(() => ID)
  selectedInterviewSlotID!: string;

  @validate(sgNonOptional())
  @Field(() => String)
  motivation!: string;

  @validate(options => {
    // check if the klasse is not elementary
    if (
      options.target.mode === RegisterMode.REGISTRATION_ELEMENTARY_ELEMENTARY ||
      options.target.mode === RegisterMode.REGISTRATION_HIGH_HIGH
    ) {
      return sgNonOptional()(options);
    }
  })
  @Field(() => String)
  schulwechselMotivation!: string;

  // #endregion

  // #region School Data

  /**
   * The ID of the potential Martinus Elementary School the student is enrolling in. If the student is not enrolling in a Martinus Elementary School, this field the name of the elementary school
   */
  @validate(options => {
    // check if the klasse is not elementary
    if (options.target.mode !== RegisterMode.REGISTRATION_NEW_ELEMENTARY) {
      return sgNonOptional()(options);
    }
  })
  @Field(() => String)
  potentialMartinusElementarySchoolID!: string;

  @validate(options => {
    // check if the klasse is not elementary
    if (options.target.potentialMartinusElementarySchoolID === `Andere Schule`) {
      return sgNonOptional()(options);
    }
  })
  @Field(() => String)
  potentialMartinusElementarySchoolName!: string;

  @Field(() => Int)
  lastGradeGerman!: number;

  @Field(() => Int)
  lastGradeMath!: number;

  @Field(() => Int)
  lastGradeSachunterricht!: number;

  @Field(() => String)
  interestInNativeLanguageCourses: Maybe<string>;

  @Field(() => Boolean)
  interestInWholeDaySchooling: Maybe<boolean>;

  @validate(options => {
    // check if the klasse is not elementary
    if (options.target.mode === RegisterMode.REGISTRATION_ELEMENTARY_HIGH) {
      return sgNonOptional()(options);
    }
  })
  @Field(() => Int)
  schoolyearsAfterElementary!: number;

  @validate(options => {
    // check if the klasse is not elementary
    if (
      options.target.mode === RegisterMode.REGISTRATION_ELEMENTARY_ELEMENTARY ||
      options.target.mode === RegisterMode.REGISTRATION_HIGH_HIGH
    ) {
      return sgNonOptional()(options);
    }
  })
  @Field(() => String)
  currentKlassenstufe!: string;

  // #endregion
  @Field(() => RegisterStudentDataFiles, { default: v => v ?? new RegisterStudentDataFiles({}) })
  files!: RegisterStudentDataFiles;

  @Field(() => [String], {
    default: v => v ?? [],
  })
  activeTrackerIDs!: string[];

  /** Address lines the applicant confirmed although no house number was recognized in them. Form state only. */
  @Field(() => [String], {
    default: v => v ?? [],
  })
  confirmedAddresses!: string[];

  @Field(() => [RegisterStudentDataSorgeberechtigte], {
    default: v => v ?? [new RegisterStudentDataSorgeberechtigte({})],
  })
  sorgeberechtigte!: RegisterStudentDataSorgeberechtigte[];
}

export const registerStudentWithSuSForm = registerMutation({
  operationName: 'registerStudentWithSuSForm',
  type: () => String,
  params: [{ type: () => RegisterStudentData, name: 'registerData' }],
  middlewares: [throttleMiddleware(5000)],
  resolve: resolveRegisterStudentWithSUSForm,
});
